import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { isEqual } from 'lodash';
import { aggregateAgentDowntimeAlarmsIntoTimeslots } from 'app/views/synthetics/utils/syntheticsUtils';
import { Flex, Spinner, Card, Text } from 'core/components';
import { getStartAndEnd } from 'core/util/dateUtils';
import LookbackDateRange from 'core/form/components/LookbackDateRange';
import { parseQueryString } from 'app/util/utils';
import moment from 'moment';
import AgentDowntimeSeries from './AgentDowntimeSeries';
import AgentDowntimeSummary from './AgentDowntimeSummary';
import AgentDowntimeTable from './AgentDowntimeTable';

@inject('$syn')
@withRouter
@observer
export default class AgentDowntime extends Component {
  state = {
    loading: true,
    alarms: null,
    resultTimeMs: undefined,
    activeTimeslot: undefined,
    selectedTime: {
      lookbackSeconds: 21600,
      startDate: null,
      endDate: null
    }
  };

  constructor(props) {
    super(props);
    const { location } = this.props;
    const { start, end } = parseQueryString(location.search);
    // we're expecting start/end dates to be unix timestamps
    const startDate = parseInt(start);
    const endDate = parseInt(end);

    if (Number.isFinite(startDate)) {
      // we'll only need a start date to override the date selection on load
      // if we define an end date as part of the query param, we'll use that, otherwise it'll fall back to 'now'
      this.state.selectedTime = {
        lookbackSeconds: null,
        startDate,
        endDate: Number.isFinite(endDate) ? endDate : moment().unix()
      };
    }
  }

  componentDidMount() {
    this.loadIncidentLogs();
  }

  componentDidUpdate(prevProps, prevState) {
    const { agent } = this.props;
    const { selectedTime } = this.state;
    const agentUpdated = prevProps.agent?.id !== agent?.id;
    const selectedTimeChanged = !isEqual(prevState.selectedTime, selectedTime);

    if (agentUpdated || selectedTimeChanged) {
      this.loadIncidentLogs();
    }
  }

  loadIncidentLogs() {
    const { $syn, agent } = this.props;
    const { selectedTime } = this.state;
    const [start, end] = getStartAndEnd(selectedTime);

    this.setState({ loading: true });

    if (agent?.agent_alert_rule_config?.alertingRuleId) {
      $syn.requests
        .fetchAlertManagerAlarms({
          ruleIds: [agent.agent_alert_rule_config.alertingRuleId],
          active: {
            start: moment.unix(start).toISOString(),
            end: moment.unix(end).toISOString()
          }
        })
        .then((resp) => {
          this.setState({
            alarms: aggregateAgentDowntimeAlarmsIntoTimeslots({
              alarms: resp.alarms,
              ...selectedTime
            }),
            loading: false
          });
        });
    }
  }

  handleDateRangeChange = (selectedTime) => {
    this.setState({ selectedTime });
  };

  handleTimelineSelect = (resultTimeMs) => {
    const { alarms } = this.state;
    const activeTimeslot = Object.values(alarms.timeslots).find((slot) => slot.time === resultTimeMs / 1000);
    const activeAlarms = alarms.alarms.filter((alarm) => activeTimeslot.alarms.includes(alarm.id));

    this.setState({
      resultTimeMs,
      activeTimeslot: activeAlarms.length > 0 ? { ...activeTimeslot, alarms: activeAlarms } : null
    });
  };

  render() {
    const { agent } = this.props;
    const { loading, alarms, activeTimeslot, resultTimeMs, selectedTime } = this.state;

    if (!agent?.agent_alert_rule_config?.alertingRuleId) {
      return <Text>Status alerting not configured</Text>;
    }

    if (loading) {
      return (
        <Flex justifyContent="center">
          <Spinner />
        </Flex>
      );
    }

    return (
      <Flex flexDirection="column" flex={1}>
        <Card p={1} mb={1}>
          <LookbackDateRange
            {...selectedTime}
            minLookbackSeconds={300}
            onChange={this.handleDateRangeChange}
            minimal
            showShortcutLabelAsInterval
          />
        </Card>
        <Flex px={2} pt={2}>
          <AgentDowntimeSeries {...selectedTime} alarms={alarms} onTimelineSelect={this.handleTimelineSelect} />
        </Flex>
        <AgentDowntimeSummary activeTimeslot={activeTimeslot} resultTimeMs={resultTimeMs} />
        <AgentDowntimeTable activeTimeslot={activeTimeslot} resultTimeMs={resultTimeMs} />
      </Flex>
    );
  }
}
